"use client";

import { useEffect, useRef, useState } from "react";

import {
  ListingCard,
  type ListingCardType,
} from "@/components/listings/ListingCard";
import { ListingCardSkeleton } from "@/components/listings/ListingCardSkeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

interface BaseListingCarouselProps {
  heading: string;
  listings: ListingCardType[] | undefined;
  isLoading?: boolean; // Optional for server components (defaults to false)
  emptyMessage: string;
  delayTime?: number; // Auto-advance delay in milliseconds, 0 = no auto-advance
}

export default function BaseListingCarousel({
  heading,
  listings,
  isLoading = false,
  emptyMessage,
  delayTime = 5000,
}: BaseListingCarouselProps) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  listings = listings ?? [];

  // Auto-advance functionality
  useEffect(() => {
    if (!api || !listings || listings.length <= 1) return;

    // Validate delayTime - must be 0 or >= 250ms
    const validDelayTime = delayTime > 0 && delayTime < 250 ? 250 : delayTime;

    if (validDelayTime === 0) return;

    const startAutoAdvance = () => {
      autoAdvanceRef.current = setInterval(() => {
        if (api && api.canScrollNext()) {
          api.scrollNext();
        } else if (api && api.canScrollPrev()) {
          // Reset to beginning when reaching the end
          api.scrollTo(0);
        }
      }, validDelayTime);
    };

    startAutoAdvance();

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
  }, [api, listings, delayTime]);

  return (
    <div className="mb-12">
      <h2 className="mb-6 text-left text-xl font-semibold">{heading}</h2>
      {isLoading ? (
        <Carousel className="w-full" opts={{ align: "start" }} setApi={setApi}>
          <CarouselContent>
            {Array.from({ length: 4 }).map((_, index) => (
              <CarouselItem key={index} className="basis-1/2">
                <ListingCardSkeleton />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      ) : listings && listings.length > 0 ? (
        <Carousel className="w-full" opts={{ align: "start" }} setApi={setApi}>
          <CarouselContent>
            {listings.map((listing: ListingCardType) => (
              <CarouselItem key={listing.id} className="basis-1/2">
                <ListingCard listing={listing} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      ) : (
        <div className="relative">
          <Carousel
            className="w-full"
            opts={{ align: "start" }}
            setApi={setApi}
          >
            <CarouselContent>
              {Array.from({ length: 4 }).map((_, index) => (
                <CarouselItem key={index} className="basis-1/2">
                  <ListingCardSkeleton />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px] dark:bg-black/40">
            <div className="rounded-lg bg-white/90 px-6 py-4 shadow-lg dark:bg-gray-800/90">
              <p className="text-center text-lg font-medium text-gray-900 dark:text-white">
                {emptyMessage}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
